/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { NgClass } from '@angular/common';
import { of, Subject } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';

import {
    NvAddKeyboardActivateDirective,
    NvFocusRippleDirective,
    OffDeviceLinkDirective,
    HyperLinkUserActivationGuardDirective
} from 'hig';
import { LoggingService, Logger, TrustAsHtmlPipe } from 'crimson';
import {
    ActiveTestDataType,
    ALTERNATE_COORDINATOR_EMAIL_TAG,
    PlaytestState,
    PlaytestMenu,
    PlaytestType,
    PLAYTEST_NO_END_TIME,
    TestState,
    Topic
} from '../shared';

import { PlaytestLiveService } from '../core/services/playtest-live.service';
import { PublicLearnArticleService } from '../core/services/public-learn-article.service';
import { PlaytestLaunchComponent } from '../playtest-launch/playtest-launch.component';
import { LearnArticleComponent } from '../learn-article/learn-article.component';
import { PostLaunchButtonComponent } from '../post-launch-button/post-launch-button.component';

@Component({
    selector: 'nv-sessions',
    templateUrl: './sessions.component.html',
    styleUrls: ['./sessions.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        ExtendedModule,
        FlexModule,
        HyperLinkUserActivationGuardDirective,
        LearnArticleComponent,
        MatButton,
        MatCard,
        MatCardContent,
        MatDivider,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatIcon,
        NgClass,
        NvAddKeyboardActivateDirective,
        NvFocusRippleDirective,
        OffDeviceLinkDirective,
        PostLaunchButtonComponent,
        TrustAsHtmlPipe
    ]
})
export class SessionsComponent implements OnInit, OnDestroy {
    readonly PlaytestMenu = PlaytestMenu;
    readonly PlaytestType = PlaytestType;
    readonly TestState = TestState;
    readonly PLAYTEST_NO_END_TIME = PLAYTEST_NO_END_TIME;

    private destroy$: Subject<boolean> = new Subject<boolean>();
    private logger: Logger;
    activeTests: ActiveTestDataType[] = [];
    selectedTest: ActiveTestDataType;
    selectedLearnTopics: Topic[] = [];

    isTestActive = false;
    showTestCard: PlaytestMenu = PlaytestMenu.playtest;
    showTestListTitles = false;
    showTestListTitlesList = false;
    showUpcoming = false;
    upcomingTestId = null;
    updateInterval = null;
    timeInHrsUpcoming = 20;
    upcomingTestTitle = `Starts in ${this.timeInHrsUpcoming} hours!`;
    userDisplayName = '';
    notesExpanded = false;

    constructor(
        private loggingService: LoggingService,
        private playtestLiveService: PlaytestLiveService,
        private learnArticleService: PublicLearnArticleService,
        private dialog: MatDialog
    ) {
        this.logger = this.loggingService.getLogger('SessionComponent');
        this.showUpcoming = false;
    }

    ngOnInit() {
        this.playtestLiveService
            .getPageState()
            .pipe(
                takeUntil(this.destroy$),
                mergeMap(state => {
                    if (this.isPageActive(state)) {
                        // Go ahead and update page as soon as we can even before fetching articles
                        this.processState();
                        return this.learnArticleService.getArticles().pipe(map(() => state));
                    }
                    return of(state);
                })
            )
            .subscribe(state => {
                if (this.isPageActive(state)) {
                    // Process state again after articles are fetched
                    this.processState();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private isPageActive(state: PlaytestState): boolean {
        return state === PlaytestState.home || state === PlaytestState.invites;
    }

    private processState() {
        this.userDisplayName = this.playtestLiveService.getUserDisplayName();
        this.showTestCard = this.playtestLiveService.getTestHomeMenuOption();
        this.activeTests = this.playtestLiveService.getPlayNowTestData();
        // this.isTestActive is getting used in HTML (view)
        this.isTestActive = this.activeTests.length > 0;
        if (this.isTestActive) {
            const nonDeclinedTest = this.activeTests.find(tests => tests.status !== 'denied');
            if (nonDeclinedTest || this.showTestCard === PlaytestMenu.manageInvite) {
                this.showTestListTitles = true;
            } else {
                this.showTestListTitles = false;
            }

            if (nonDeclinedTest && this.showTestCard === PlaytestMenu.playtest && !this.selectedTest) {
                // Default to first non-declined test, but then pic earliest active test
                this.selectedTest = this.activeTests.find(test => test.playStatus === TestState.playnow);
                if (!this.selectedTest) {
                    this.selectedTest = nonDeclinedTest;
                }
            }

            if (this.selectedTest && this.selectedTest.tags && this.selectedTest.tags.length > 0) {
                this.selectedLearnTopics = this.learnArticleService.getTopicsForTag(this.selectedTest.tags);
            } else {
                this.selectedLearnTopics = [];
            }

            this.showTestListTitlesList = true;
            if (this.showTestCard === PlaytestMenu.playtest && this.selectedTest && this.activeTests.length === 1) {
                const test = this.activeTests[0];
                if (test.testType === PlaytestType.Beta || test.status === 'accepted') {
                    this.showTestListTitlesList = false;
                }
            }
        } else {
            this.showTestListTitles = false;
            this.showTestListTitlesList = false;
        }

        // Experimental
        const retVal = this.playtestLiveService.isTestActiveOrUpcoming();
        if (retVal === TestState.upcoming) {
            const testId = this.playtestLiveService.getEarliestUpcomingTestId();
            this.showUpcoming = true;
            this.upcomingTestId = testId;
            this.timeInHrsUpcoming = this.getTimeToUpcomingTest(testId);
            this.updateUpcomingTestTitle();
        } else {
            this.showUpcoming = false;
            this.upcomingTestId = null;
        }
    }

    private updateUpcomingTestTitle() {
        this.upcomingTestTitle = `Starts in ${this.timeInHrsUpcoming} hours!`;
    }

    onContactClicked(coordEmail) {
        const mailtoText = 'mailto:' + coordEmail;
        window.open(mailtoText, '_self');
    }

    onLaunchButtonClicked(test: ActiveTestDataType) {
        const testId = test.testId;
        if (testId) {
            this.playtestLiveService.setLaunchTestId(testId);
            const dialog = this.dialog.open(PlaytestLaunchComponent, {
                panelClass: ['nv-hig', 'nv-igo'],
                disableClose: false,
                hasBackdrop: true,
                autoFocus: false
            });
        } else {
            this.logger.error('Error: Launch test failed. Cause = Invalid test');
        }
    }

    onTestSetupButtonClicked(index: any) {
        // Set menu option selected as Settings
        this.playtestLiveService.setTestHomeMenuOption(PlaytestMenu.settings);
        // Set current page state to settings page.
        this.playtestLiveService.setCurrentPageState(PlaytestState.settings);
    }

    onClickTestResponse(testId, response: string) {
        this.playtestLiveService.processTestStatusChange(testId, response);
    }

    onSelectTest(test: ActiveTestDataType) {
        this.selectedTest = test;
        if (this.selectedTest.tags && this.selectedTest.tags.length > 0) {
            this.selectedLearnTopics = this.learnArticleService.getTopicsForTag(this.selectedTest.tags);
        } else {
            this.selectedLearnTopics = [];
        }
    }

    getTimeToUpcomingTest(Id: string) {
        let diffTime = 0;
        const testIdx = this.activeTests.findIndex(test => test.testId === Id);
        if (testIdx >= 0 && testIdx < this.activeTests.length) {
            const nowTime = new Date().getTime();
            const startTime = this.activeTests[testIdx].startTime.getTime();
            if (nowTime < startTime) {
                diffTime = Math.round((startTime - nowTime) / (1000 * 60 * 60)); // In hours
            }
        }
        return diffTime;
    }

    onClickInvites() {
        this.playtestLiveService.setTestHomeMenuOption(PlaytestMenu.manageInvite);
        this.playtestLiveService.setCurrentPageState(PlaytestState.invites);
    }

    getTestStatus(test: ActiveTestDataType): string {
        if (test) {
            switch (test.playStatus) {
                case TestState.expired:
                    return 'Expired';

                case TestState.playnow:
                    return 'Active';

                case TestState.upcoming:
                    return 'Upcoming';

                default:
                    return '';
            }
        }
        return '';
    }

    hasEndTime(test?: ActiveTestDataType): boolean {
        return test.endTime.getTime() !== PLAYTEST_NO_END_TIME.getTime();
    }

    getCoordinatorEmail(test?: ActiveTestDataType): string {
        if (test) {
            const alternateCoordinatorEmail = test.tags.find((tag: string) =>
                tag.includes(ALTERNATE_COORDINATOR_EMAIL_TAG)
            );
            if (alternateCoordinatorEmail) {
                const parts = alternateCoordinatorEmail.split(ALTERNATE_COORDINATOR_EMAIL_TAG);
                if (parts.length > 1) {
                    return parts[1];
                }
            }
            return test.coordEmail;
        }
        return '';
    }

    getTestRuns(test?: ActiveTestDataType): number {
        if (test) {
            const stats = this.playtestLiveService.getTestStats(test.testId);
            return stats.totalRuns;
        }
        return 0;
    }
}
